// @ts-nocheck
/**
 * ExpandablePanel — generic render-prop based expandable list system.
 *
 *  ExpandableCard   – single accordion card: handles expand/collapse chrome,
 *                     left accent bar, ring/glow, built-in chevron.
 *
 *  ExpandablePanel  – list container: toolbar slot, skeleton, error state,
 *                     empty state, item list, load-more / pagination chrome.
 */
import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import {
  LuChevronDown,
  LuChevronUp,
  LuRefreshCw,
  LuLoader,
  LuInbox,
} from 'react-icons/lu';
import {
  APP_BG,
  APP_BORDER,
  APP_TEXT,
  APP_SEMANTIC,
} from '@/constant/theme/colors.constant';

// ─────────────────────────────────────────────────────────────────────────────
// ExpandableCard
// ─────────────────────────────────────────────────────────────────────────────

export interface ExpandableCardProps {
  /** Whether this card is currently expanded */
  expanded: boolean;
  /** Called when the header row is clicked */
  onToggle: () => void;
  /**
   * Header content rendered inside the toggle button.
   * Receives `expanded` so you can conditionally style your own elements.
   */
  header: React.ReactNode | ((expanded: boolean) => React.ReactNode);
  /** Panel content shown when expanded */
  children?: React.ReactNode;
  /**
   * CSS colour string for the left accent bar (e.g. "#6366f1").
   * Omit to hide the bar.
   */
  accentColor?: string;
  /**
   * Tailwind ring classes applied when expanded.
   * @default "ring-2 ring-indigo-300/40 dark:ring-indigo-700/30"
   */
  expandedRing?: string;
  /**
   * Tailwind shadow classes applied when expanded.
   * @default "shadow-lg shadow-indigo-100/60 dark:shadow-indigo-900/20"
   */
  expandedShadow?: string;
  /**
   * Whether to show the built-in chevron button.
   * Pass `false` when your header renders its own toggle indicator.
   * @default true
   */
  showChevron?: boolean;
  /** `data-testid` on the toggle button */
  testId?: string;
  className?: string;
  /** Extra classes on the toggle button */
  headerClassName?: string;
  /** Background tint class on the header when expanded */
  expandedHeaderBg?: string;
  /** Whether to include the border-t separator before the expanded panel */
  panelDivider?: boolean;
}

export const ExpandableCard: React.FC<ExpandableCardProps> = ({
  expanded,
  onToggle,
  header,
  children,
  accentColor,
  expandedRing = 'ring-2 ring-indigo-300/40 dark:ring-indigo-700/30',
  expandedShadow = 'shadow-lg shadow-indigo-100/60 dark:shadow-indigo-900/20',
  showChevron = true,
  testId,
  className,
  headerClassName,
  expandedHeaderBg = 'bg-primary/[0.04] dark:bg-primary/[0.08]',
  panelDivider = true,
}) => {
  return (
    <div
      className={classNames(
        'group rounded-xl overflow-hidden transition-all duration-200',
        APP_BG.base,
        expanded
          ? classNames(
            'border border-transparent',
            expandedRing,
            expandedShadow,
          )
          : classNames(
            'border',
            APP_BORDER.default,
            'hover:border-transparent',
            'hover:shadow-sm',
          ),
        className,
      )}
    >
      {/* ── Toggle header ── */}
      <button
        data-testid={testId ?? ''}
        type="button"
        aria-expanded={expanded}
        className={classNames(
          'relative w-full flex items-center gap-3.5 text-left transition-colors duration-150',
          headerClassName ?? 'pl-5 pr-4 py-3.5',
          expanded
            ? expandedHeaderBg
            : 'hover:bg-gray-50/60 dark:hover:bg-white/[0.015]',
        )}
        onClick={onToggle}
      >
        {/* Left accent bar */}
        {accentColor && (
          <div
            className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl transition-opacity duration-200"
            style={{ background: accentColor, opacity: expanded ? 1 : 0.5 }}
          />
        )}

        {/* Header content */}
        <div className="flex-1 min-w-0">
          {typeof header === 'function' ? header(expanded) : header}
        </div>

        {/* Built-in chevron */}
        {showChevron && (
          <div
            className={classNames(
              'shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm',
              expanded
                ? 'bg-indigo-500 text-white'
                : classNames(
                  APP_BG.muted,
                  APP_TEXT.soft,
                  'group-hover:bg-indigo-100 dark:group-hover:bg-indigo-950/50 group-hover:text-indigo-500',
                ),
            )}
          >
            {expanded ? (
              <LuChevronUp className="w-3.5 h-3.5" />
            ) : (
              <LuChevronDown className="w-3.5 h-3.5" />
            )}
          </div>
        )}
      </button>

      {/* ── Expanded panel ── */}
      {expanded && children && (
        <div
          className={classNames(
            panelDivider && classNames('border-t', APP_BORDER.soft),
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ExpandablePanel
// ─────────────────────────────────────────────────────────────────────────────

export interface ExpandablePanelProps<T = any> {
  // ── Data ──────────────────────────────────────────────────────────────────
  items: T[];
  /** Return the unique string id for an item */
  getItemId: (item: T) => string;
  /**
   * Render a single item.
   * Receives: item data, isExpanded, and an onToggle callback.
   * Wrap your content in `<ExpandableCard>` for the accordion chrome.
   */
  renderItem: (
    item: T,
    expanded: boolean,
    onToggle: () => void,
  ) => React.ReactNode;

  // ── Loading ───────────────────────────────────────────────────────────────
  loading?: boolean;
  loadingMore?: boolean;
  /** How many skeleton rows to render while loading. @default 4 */
  skeletonCount?: number;
  /** Override the default skeleton row renderer */
  renderSkeleton?: (index: number) => React.ReactNode;

  // ── Error ─────────────────────────────────────────────────────────────────
  error?: string | null;
  errorTitle?: string;
  onRetry?: () => void;

  // ── Empty state ───────────────────────────────────────────────────────────
  emptyIcon?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;

  // ── Toolbar ───────────────────────────────────────────────────────────────
  /** Rendered above the list, separated by a border-b */
  toolbar?: React.ReactNode;

  // ── Load-more ─────────────────────────────────────────────────────────────
  hasMore?: boolean;
  totalRecords?: number;
  loadedCount?: number;
  onLoadMore?: () => void;

  // ── Expand management ─────────────────────────────────────────────────────
  /** Controlled: currently expanded item id */
  expandedId?: string | null;
  /** Called when an item is toggled. Also works as uncontrolled notifier. */
  onExpandChange?: (id: string | null) => void;
  /** Allow multiple items expanded simultaneously */
  multiExpand?: boolean;

  // ── Layout ────────────────────────────────────────────────────────────────
  /** Gap between item rows. @default "md" */
  gap?: 'none' | 'sm' | 'md';
  /** Wrap the list body in an overflow-auto scrollable region */
  scrollable?: boolean;
  className?: string;
  listClassName?: string;
}

const GAP_CLASS: Record<NonNullable<ExpandablePanelProps['gap']>, string> = {
  none: 'gap-0',
  sm: 'gap-1.5',
  md: 'gap-2',
};

const DefaultSkeleton = () => {
  return (
    <div
      className={classNames(
        'rounded-2xl border p-4 animate-pulse',
        APP_BORDER.default,
        APP_BG.base,
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={classNames('w-9 h-9 rounded-xl shrink-0', APP_BG.muted)}
        />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <div className={classNames('h-4 w-28 rounded-lg', APP_BG.muted)} />
            <div
              className={classNames('h-4 w-16 rounded-full', APP_BG.muted)}
            />
          </div>
          <div className="flex gap-3">
            <div className={classNames('h-3 w-24 rounded', APP_BG.elevated)} />
            <div className={classNames('h-3 w-32 rounded', APP_BG.elevated)} />
          </div>
        </div>
        <div className={classNames('h-5 w-20 rounded-full', APP_BG.muted)} />
      </div>
    </div>
  );
};

export function ExpandablePanel<T = any>({
  items,
  getItemId,
  renderItem,
  loading = false,
  loadingMore = false,
  skeletonCount = 4,
  renderSkeleton,
  error,
  errorTitle = 'Failed to load data',
  onRetry,
  emptyIcon,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyAction,
  toolbar,
  hasMore = false,
  totalRecords,
  loadedCount,
  onLoadMore,
  expandedId: controlledId,
  onExpandChange,
  multiExpand = false,
  gap = 'md',
  scrollable = false,
  className,
  listClassName,
}: ExpandablePanelProps<T>) {
  const isControlled = controlledId !== undefined;
  const [internalSingleId, setInternalSingleId] = useState<string | null>(null);
  const [internalMultiIds, setInternalMultiIds] = useState<Set<string>>(
    new Set(),
  );

  const isExpanded = useCallback(
    (id: string) => {
      if (isControlled) return controlledId === id;
      if (multiExpand) return internalMultiIds.has(id);
      return internalSingleId === id;
    },
    [
      isControlled,
      controlledId,
      multiExpand,
      internalMultiIds,
      internalSingleId,
    ],
  );

  const handleToggle = useCallback(
    (id: string) => {
      if (isControlled) {
        onExpandChange?.(controlledId === id ? null : id);
        return;
      }
      if (multiExpand) {
        setInternalMultiIds((prev) => {
          const next = new Set(prev);
          next.has(id) ? next.delete(id) : next.add(id);
          return next;
        });
      } else {
        const next = internalSingleId === id ? null : id;
        setInternalSingleId(next);
        onExpandChange?.(next);
      }
    },
    [isControlled, controlledId, multiExpand, internalSingleId, onExpandChange],
  );

  // ── Error ──
  if (error) {
    return (
      <div className={classNames('flex flex-col', className)}>
        {toolbar && (
          <div className={classNames('border-b', APP_BORDER.soft)}>
            {toolbar}
          </div>
        )}
        <div
          className={classNames(
            'm-5 flex flex-col items-center gap-5 rounded-2xl border py-14 px-6 text-center',
            APP_SEMANTIC.danger.bg,
            APP_SEMANTIC.danger.border,
          )}
        >
          <p
            className={classNames(
              'text-sm font-bold',
              APP_SEMANTIC.danger.text,
            )}
          >
            {errorTitle}
          </p>
          <p
            className={classNames(
              'text-xs max-w-sm leading-relaxed',
              APP_TEXT.muted,
            )}
          >
            {error}
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold bg-red-600 hover:bg-red-700 text-white transition-colors shadow-sm active:scale-95"
            >
              <LuRefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Empty ──
  if (!loading && items.length === 0) {
    return (
      <div className={classNames('flex flex-col', className)}>
        {toolbar && (
          <div className={classNames('border-b', APP_BORDER.soft)}>
            {toolbar}
          </div>
        )}
        <div className="flex flex-col items-center justify-center py-24 gap-5 px-8 text-center">
          <div
            className={classNames(
              'relative w-16 h-16 rounded-2xl flex items-center justify-center border',
              APP_BORDER.default,
              APP_BG.surface,
              'shadow-sm',
            )}
          >
            {emptyIcon ?? (
              <LuInbox className={classNames('w-7 h-7', APP_TEXT.subtle)} />
            )}
          </div>
          <div className="space-y-1.5">
            <p
              className={classNames('text-sm font-semibold', APP_TEXT.default)}
            >
              {emptyTitle}
            </p>
            {emptyDescription && (
              <p
                className={classNames(
                  'text-xs leading-relaxed max-w-[240px]',
                  APP_TEXT.muted,
                )}
              >
                {emptyDescription}
              </p>
            )}
          </div>
          {emptyAction}
        </div>
      </div>
    );
  }

  const listBody = (
    <>
      {loading ? (
        <div
          className={classNames(
            'flex flex-col p-4',
            GAP_CLASS[gap],
            listClassName,
          )}
        >
          {Array.from({ length: skeletonCount }, (_, i) =>
            renderSkeleton ? renderSkeleton(i) : <DefaultSkeleton key={i} />,
          )}
        </div>
      ) : (
        <div
          className={classNames(
            'flex flex-col p-4',
            GAP_CLASS[gap],
            listClassName,
          )}
        >
          {items.map((item) => {
            const id = getItemId(item);
            return (
              <React.Fragment key={id}>
                {renderItem(item, isExpanded(id), () => handleToggle(id))}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Load more */}
      {hasMore && !loading && (
        <div className="flex flex-col items-center gap-1.5 px-4 pb-6 pt-1">
          <div className="flex items-center gap-3 w-full max-w-[200px]">
            <div
              className={classNames('flex-1 h-px bg-gray-200 dark:bg-gray-700')}
            />
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className={classNames(
                'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 disabled:opacity-50 active:scale-95 border',
                loadingMore
                  ? classNames(
                    APP_BORDER.default,
                    APP_BG.elevated,
                    APP_TEXT.muted,
                  )
                  : 'border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-950/50',
              )}
            >
              {loadingMore ? (
                <>
                  <LuLoader className="w-3 h-3 animate-spin" /> Loading…
                </>
              ) : (
                <>
                  <LuChevronDown className="w-3 h-3" /> Load more
                </>
              )}
            </button>
            <div
              className={classNames('flex-1 h-px bg-gray-200 dark:bg-gray-700')}
            />
          </div>
          {totalRecords != null && loadedCount != null && (
            <p
              className={classNames(
                'text-[10px] tabular-nums',
                APP_TEXT.subtle,
              )}
            >
              {loadedCount} of {totalRecords} records
            </p>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className={classNames('flex flex-col min-h-0', className)}>
      {toolbar && (
        <div className={classNames('border-b', APP_BORDER.soft)}>{toolbar}</div>
      )}
      {scrollable ? (
        <div className="flex-1 overflow-auto">{listBody}</div>
      ) : (
        listBody
      )}
    </div>
  );
}
